import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Box, Link } from '@mui/material';

import { AuditEvent } from './auditData';
import { getWorkflowBuilderUrl } from 'src/pages/intent-conversation-analytics/helpers';
import { useGetAutoflowVersionHistoryV2Query } from 'src/services/workflowBuilderAutoflowApi.ts/workflowBuilderAutoflowApi';
import { setGlobalToastOptions } from 'src/slices/ui/uiSlice';

// The V2 history endpoint serializes an unset version name as the string
// "None" (the Mongo field is optional and goes through str()).
const isVersionNamed = (versionName: string) =>
  !!versionName && versionName !== 'None';

// The policy-picked line when the picked workflow is known: the version label
// resolved from that workflow's version history, the whole line deeplinking to
// the exact version in the builder. A version the archive recorded but the
// history no longer lists is gone — the line links to the live version
// instead, with a toast saying so on click. Without a recorded version (old
// archives), the line links to the workflow with nothing picked.
export const WorkflowVersionLink = ({ event }: { event: AuditEvent }) => {
  const { t } = useTranslation('intent-conversation-analytics');
  const dispatch = useDispatch();
  const link = event.workflowLink!;
  const {
    data: versionHistory,
    isError,
    isLoading,
  } = useGetAutoflowVersionHistoryV2Query(link.workflowId);

  const recorded = link.version;
  const matched =
    recorded !== undefined
      ? versionHistory?.find(version => version.version === recorded)
      : undefined;
  // Only a loaded history can vouch for a version: a failed call degrades to
  // the live link rather than risking a builder URL that cannot resolve.
  const isVersionGone =
    recorded !== undefined &&
    !isLoading &&
    !isError &&
    versionHistory !== undefined &&
    matched === undefined;

  // The customer's own name for the version when they gave one; the version
  // number when they didn't, and while the history is still loading.
  const versionLabel =
    matched && isVersionNamed(matched.version_name)
      ? matched.version_name
      : recorded !== undefined
        ? String(recorded)
        : undefined;

  const detail =
    versionLabel !== undefined
      ? t('conversationAudit.detailIntentWithVersion', {
          intent: link.intentTitle,
          version: versionLabel,
        })
      : event.detail;

  const href = getWorkflowBuilderUrl({
    channel: 'widget',
    intentWorkflowId: link.workflowId,
    mode: 'autoflow',
    ...(matched
      ? { version: matched.version, versionType: 'saved' as const }
      : {}),
  });

  const handleClick = () => {
    if (!isVersionGone) {
      return;
    }
    dispatch(
      setGlobalToastOptions({
        autoHideDuration: 5000,
        subtitle: t('conversationAudit.versionUnavailableSubtitle'),
        title: t('conversationAudit.versionUnavailableTitle'),
        variant: 'danger',
      }),
    );
  };

  return (
    <Box>
      <Link
        href={href}
        onClick={handleClick}
        rel='noopener noreferrer'
        target='_blank'
        variant='font12'
      >
        {detail}
      </Link>
    </Box>
  );
};
